import get from 'lodash/get';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { HorizontalLayout, VerticalLayout } from '../../../Layout';
import { Text } from '../../../Text';
import { useFieldValidation } from '../../Form';
import { FormLabel } from '../FormCommons';
import { FormRadioButton } from './FormRadioButton';

interface FormRadioButtonsProps {
    name: string;
    title: string;
    subtitle?: string;
    values: { value: string | number; label: string }[];
    disabled?: boolean;
    required?: boolean;
    horizontal?: boolean;
    placeholder?: string;
    variant?: 'default' | 'outline';
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

export const FormRadioButtons = ({
    name,
    title,
    variant = 'default',
    subtitle,
    values,
    required,
    horizontal,
    placeholder,
    onChange,
}: FormRadioButtonsProps) => {
    const {
        formState: { errors },
    } = useFormContext();
    const error = get(errors, name);
    const { isRequired } = useFieldValidation(name);
    const Layout = (horizontal ? HorizontalLayout : VerticalLayout) as React.ElementType;
    const isOutlineVariant = variant === 'outline';

    return (
        <VerticalLayout $gap={8}>
            <FormLabel as="span">
                {title}
                {(required || isRequired) && '*'}
            </FormLabel>
            <VerticalLayout $gap={4} $padding={isOutlineVariant ? '0' : horizontal ? '0' : '0 0 0 16px'}>
                {subtitle && <Text $themeColor="subtle">{subtitle}</Text>}
                <Layout $gap={horizontal ? 32 : 8}>
                    {values.length > 0 ? (
                        values.map(({ value, label }) => (
                            <FormRadioButton
                                key={value}
                                name={name}
                                value={value}
                                label={label}
                                variant={variant}
                                onChange={onChange}
                            />
                        ))
                    ) : (
                        <Text $italic $themeColor="subtle">
                            {placeholder}
                        </Text>
                    )}
                </Layout>
                {error && <Text $themeColor="negative">{error.message?.toString()}</Text>}
            </VerticalLayout>
        </VerticalLayout>
    );
};
